import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import BASE_URL from "../../config/config";

const SiteSettings = () => {
  const [smsConfig, setSmsConfig] = useState({   
    lineNumber: "",
    useSandbox: false,
    templates: {
      verification: "",
      purchase: "",
      welcome: ""
    }
  });
  
  const [testMessage, setTestMessage] = useState({
    mobile: "",
    templateId: "",
    text: "",
    parameters: [{ name: "Code", value: "123456" }]
  });
  
  const [loading, setLoading] = useState(false);
  const [savedConfig, setSavedConfig] = useState(false);
  const [testMethod, setTestMethod] = useState("verify"); // verify, bulk
  const [csrfToken, setCsrfToken] = useState("");
  
  // تنظیم هدرهای axios با توکن احراز هویت
  const authAxios = axios.create({
    baseURL: BASE_URL,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    withCredentials: true // اضافه کردن این گزینه برای ارسال کوکی‌ها
  });
  
  // دریافت CSRF Token
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/csrf-token`, {
          withCredentials: true,
        });
        setCsrfToken(response.data.csrfToken);
        
        // تنظیم توکن CSRF به عنوان هدر پیش‌فرض برای تمام درخواست‌ها
        if (response.data.csrfToken) {
          authAxios.defaults.headers.common['CSRF-Token'] = response.data.csrfToken;
        }
      } catch (error) {
        console.error("Error fetching CSRF token:", error);
      }
    };
  
    fetchCsrfToken();
  }, []);
  
  // Load existing SMS config if available
  useEffect(() => {
    const fetchSmsConfig = async () => {
      try {
        setLoading(true);
        const response = await authAxios.get(`${BASE_URL}/admin/sms-config`);
        if (response.data.success) {
          setSmsConfig(response.data.data);
          setSavedConfig(true);
        }
      } catch (error) {
        console.error("Error fetching SMS config:", error);
        toast.error(error.response?.data?.message || "خطا در دریافت تنظیمات پیامک");
      } finally {
        setLoading(false);
      }
    };
    
    fetchSmsConfig();
  }, []);
  
  const handleConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      // Handle nested properties (templates)
      const [parent, child] = name.split('.');
      setSmsConfig({
        ...smsConfig,
        [parent]: {
          ...smsConfig[parent],
          [child]: value
        }
      });
    } else {
      // Handle regular properties
      setSmsConfig({
        ...smsConfig,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };
  
  const handleTestMessageChange = (e) => {
    const { name, value } = e.target;
    setTestMessage({
      ...testMessage,
      [name]: value
    });
  };
  
  const handleParameterChange = (index, field, value) => {
    const updatedParams = [...testMessage.parameters];
    updatedParams[index] = { ...updatedParams[index], [field]: value };
    setTestMessage({
      ...testMessage,
      parameters: updatedParams
    });
  };
  
  const addParameter = () => {
    setTestMessage({
      ...testMessage,
      parameters: [...testMessage.parameters, { name: "", value: "" }]
    });
  };
  
  const removeParameter = (index) => {
    const updatedParams = testMessage.parameters.filter((_, i) => i !== index);
    setTestMessage({
      ...testMessage,
      parameters: updatedParams
    });
  };
  
  const saveConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await authAxios.post(`${BASE_URL}/admin/sms-config`, smsConfig, {
        headers: {  
        'Authorization': `Bearer ${token}`,       
          'CSRF-Token': csrfToken
        },
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success("تنظیمات پیامک با موفقیت ذخیره شد");
        setSavedConfig(true);
      }
    } catch (error) {
      console.error("Error saving SMS config:", error);
      toast.error(error.response?.data?.message || "خطا در ذخیره تنظیمات پیامک");
    } finally {
      setLoading(false);
    }
  };
  
  const sendTestMessage = async () => {
    try {
      setLoading(true);
      
      // انتخاب API مناسب بر اساس روش تست
      const endpoint = testMethod === "verify" 
        ? `${BASE_URL}/admin/test-sms/verify` 
        : `${BASE_URL}/admin/test-sms/bulk`;
      
      // اطمینان از ارسال داده‌های صحیح
      let requestData = {
        mobile: testMessage.mobile
      };
      
      if (testMethod === "verify") {
        requestData.templateId = testMessage.templateId;
        requestData.parameters = testMessage.parameters.filter(p => p.name && p.value);
      } else {
        requestData.text = testMessage.text;
      }
      
      // console.log("Sending request to:", endpoint);
      // console.log("Request data:", requestData);
      
      const token = localStorage.getItem('token');

      const response = await axios.post(endpoint, requestData, {
        headers: {  
        'Authorization': `Bearer ${token}`,       
          'CSRF-Token': csrfToken
        },
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success("پیامک تست با موفقیت ارسال شد");
      }
    } catch (error) {
      console.error("Error sending test SMS:", error);
      
      // نمایش پیام خطای دقیق‌تر
      if (error.response) {
        // خطای سرور با کد وضعیت
        toast.error(`خطا: ${error.response.status} - ${error.response.data.message || 'خطای نامشخص'}`);
      } else if (error.request) {
        // خطای عدم دریافت پاسخ
        toast.error("خطا: سرور پاسخی نداد");
      } else {
        // سایر خطاها
        toast.error(`خطا: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">تنظیمات سایت</h1>
      
      {/* بخش تنظیمات پیامک */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold mb-4">تنظیمات پیامک</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* حذف فیلد کلید API */}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              شماره خط
            </label>
            <input
              type="text"
              name="lineNumber"
              value={smsConfig.lineNumber}
              onChange={handleConfigChange}
              className="w-full p-2 border rounded-md"
              placeholder="شماره خط ارسال پیامک"
            />
          </div>
          
          <div className="col-span-1 md:col-span-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="useSandbox"
                checked={smsConfig.useSandbox}
                onChange={handleConfigChange}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                استفاده از محیط آزمایشی (Sandbox)
              </span>
            </label>
          </div>
        </div>
        
        <h3 className="text-lg font-medium mb-3">قالب‌های پیامک</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              قالب احراز هویت
            </label>
            <input
              type="text"
              name="templates.verification"
              value={smsConfig.templates.verification}
              onChange={handleConfigChange}
              className="w-full p-2 border rounded-md"
              placeholder="شناسه قالب احراز هویت"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              قالب خرید
            </label>
            <input
              type="text"
              name="templates.purchase"
              value={smsConfig.templates.purchase}
              onChange={handleConfigChange}
              className="w-full p-2 border rounded-md"
              placeholder="شناسه قالب خرید"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              قالب خوش‌آمدگویی
            </label>
            <input
              type="text"
              name="templates.welcome"
              value={smsConfig.templates.welcome}
              onChange={handleConfigChange}
              className="w-full p-2 border rounded-md"
              placeholder="شناسه قالب خوش‌آمدگویی"
            />
          </div>
        </div>
        
        <button
          onClick={saveConfig}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
        >
          {loading ? "در حال ذخیره..." : "ذخیره تنظیمات"}
        </button>
      </div>
      
      {/* بخش تست پیامک */}
      {savedConfig && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">تست ارسال پیامک</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              روش تست
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="testMethod"
                  value="verify"
                  checked={testMethod === "verify"}
                  onChange={() => setTestMethod("verify")}
                  className="mr-2"
                />
                <span>ارسال کد تایید</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="testMethod"
                  value="bulk"
                  checked={testMethod === "bulk"}
                  onChange={() => setTestMethod("bulk")}
                  className="mr-2"
                />
                <span>ارسال پیامک عادی</span>
              </label>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                شماره موبایل
              </label>
              <input
                type="text"
                name="mobile"
                value={testMessage.mobile}
                onChange={handleTestMessageChange}
                className="w-full p-2 border rounded-md"
                placeholder="مثال: 09123456789"
              />
            </div>
            
            {testMethod === "verify" ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  شناسه قالب
                </label>
                <input
                  type="text"
                  name="templateId"
                  value={testMessage.templateId}
                  onChange={handleTestMessageChange}
                  className="w-full p-2 border rounded-md"
                  placeholder="شناسه قالب پیامک"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  متن پیامک
                </label>
                <textarea
                  name="text"
                  value={testMessage.text}
                  onChange={handleTestMessageChange}
                  className="w-full p-2 border rounded-md"
                  placeholder="متن پیامک را وارد کنید"
                  rows="3"
                ></textarea>
              </div>
            )}
          </div>
          
          {testMethod === "verify" && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium">پارامترهای قالب</h3>
                <button
                  type="button"
                  onClick={addParameter}
                  className="text-blue-600 hover:text-blue-800"
                >
                  + افزودن پارامتر
                </button>
              </div>
              
              {testMessage.parameters.map((param, index) => (
                <div key={index} className="flex space-x-4 mb-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={param.name}
                      onChange={(e) => handleParameterChange(index, "name", e.target.value)}
                      className="w-full p-2 border rounded-md"
                      placeholder="نام پارامتر"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={param.value}
                      onChange={(e) => handleParameterChange(index, "value", e.target.value)}
                      className="w-full p-2 border rounded-md"
                      placeholder="مقدار پارامتر"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeParameter(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    حذف
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <button
            onClick={sendTestMessage}
            disabled={loading || !testMessage.mobile}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:bg-green-300"
          >
            {loading ? "در حال ارسال..." : "ارسال پیامک تست"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SiteSettings;
